import { useEffect, useRef } from "react";

import { Box, Divider, Grid, Typography } from "@mui/material"
import { ContainerCard, ContainerCharts, ContainerGraphics, ContainerGroup, CustomTitleGraphics, GridCard } from "./styles"

import { CardGraphic } from "../CardGraphic";

import DescriptionIcon from "@mui/icons-material/Description";
import GroupsIcon from "@mui/icons-material/Groups";
import CancelIcon from "@mui/icons-material/Cancel";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import AirIcon from "@mui/icons-material/Air";
import LockIcon from "@mui/icons-material/Lock";
import { useTranslation } from "react-i18next";


export const MapDashboard = ({ data }) => {

    const chartRef = useRef(null);
    const { t } = useTranslation();

    // Type data
    const cold = Number(data?.coldFlows) || 0;
    const hot = Number(data?.hotFlows) || 0;
    const electric = Number(data?.electricFlows) || 0;
    const totalWorkpermitType = cold + hot + electric;
    const noDataDonut = totalWorkpermitType === 0;
    const seriesWorkpermitType = noDataDonut
    ? [100]
    : [
        Math.round((hot / totalWorkpermitType) * 100),
        Math.round((cold / totalWorkpermitType) * 100),
        Math.round((electric / totalWorkpermitType) * 100),
    ];

    // Risk data
    const low = Number(data?.lowLevel) || 0;
    const medium = Number(data?.mediumLevel) || 0;
    const high = Number(data?.highLevel) || 0;
    const totalRisk = low + medium + high;
    const noDataRisk = totalRisk === 0;
    const pct = (v) => (totalRisk > 0 ? Math.round((v / totalRisk) * 100) : 0);
    const lowPct = pct(low);
    const mediumPct = pct(medium);
    const highPct = pct(high);
    // Donut usa porcentajes para que el arco sea proporcional
    const seriesRisk = [lowPct, mediumPct, highPct];
    const seriesBarTotal = [low, medium, high];

    // options Donut
    const donutOptions = {
        chart: { type: 'donut' },
        colors: noDataDonut ? ["#E0E0E0"] : ["#EB5757", "#1271DE", "#E5C24A"],
        labels: noDataDonut ? [t("map.noData")] : [t("map.hot"), t("map.cold"), t("map.electric")],
        legend: { position: 'right' },
        dataLabels: {
            formatter: function (val, opts) {
                // return opts.w.config.series[opts.seriesIndex];
                const value = opts.w.config.series[opts.seriesIndex];
                return `${value}%`;
            }
        },
        tooltip: {
            y: {
                formatter: (val, opts) => {
                    const realValues = [hot, cold, electric];
                    return realValues[opts.seriesIndex];
                }
            }
        },
    };

    // options Radial
    const radialOptions = {
        chart: { id: "riskDonut", type: 'donut', sparkline: { enabled: true } },
        colors: noDataRisk ? ["#E0E0E0"] : ["#6CC070", "#E5C24A", "#EB5757"],
        labels: noDataRisk ? [t("map.noData")] : [t("map.low"), t("map.medium"), t("map.high")],
        stroke: { width: 0 },
        plotOptions: {
            pie: {
                startAngle: -90,
                endAngle: 90,
                offsetY: 20,
                donut: {
                    size: "80%",

                    labels: {
                        show: true,

                        total: {
                            show: true,
                            label: noDataRisk ? t("map.noDataUpper") : t("map.total"),
                            fontSize: "12px",
                            formatter: () => (noDataRisk ? "" : totalRisk),
                        },

                        value: { show: false },
                        name: { show: false }
                    }
                }
            }
        },
        dataLabels: { 
            enabled: !noDataRisk,
            formatter: function (val, opts) { 
                const value = opts.w.config.series[opts.seriesIndex]; 
                return `${value}%`; 
            } 
        },
        tooltip: {
            enabled: true,
            y: {
                // formatter: (val) => `${val}%`
                formatter: (val, opts) => {
                    return seriesBarTotal[opts.seriesIndex];
                }
            },
        },
        legend: { show: false },
        grid: { padding: { top: -30, bottom: -30 } },
    };

    const barOptions = {
        chart: {
            type: "bar",
            sparkline: { enabled: true }
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: "75%",
                borderRadius: 6,
                distributed: true
            }
        },
        colors: ["#6CC070", "#E5C24A", "#EB5757" ], // Bajo, Medio, Alto (orden visual)
        xaxis: {
            categories: [t("map.low"), t("map.medium"), t("map.high")],
            labels: { show: false },
        },
        yaxis: {
            show: false,
            labels: {
                style: { fontSize: "12px" }
            }
        },
        grid: { show: false },
        tooltip: {
            enabled: true,
            custom: ({ series, seriesIndex, dataPointIndex, w }) => {
                const label = w.globals.labels[dataPointIndex];
                // const value = series[seriesIndex][dataPointIndex];
                const value = seriesBarTotal[dataPointIndex];

                return `
                    <div style="
                        padding:6px 10px;
                        font-size:12px;
                        font-weight:500;
                    ">
                        ${label}: ${value}
                    </div>
                `;
            }
        },
        dataLabels: {
            enabled: true,
            formatter: (val) => `${val}%`,
            offsetX: 10,
            style: { fontSize: "11px", colors: ["#fff"] }
        }
    };

    const barSeries = [
        { data: [lowPct, mediumPct, highPct] } // mismo orden que categories
    ];

    return (
        <ContainerGraphics sx={{ flex: 1, marginTop: '4px' }}>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={12} lg={6}>
                    <GridCard container alignItems="center">
                        <Grid item sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <div
                                style={{
                                    background: "#E3F2FD",
                                    borderRadius: "50%",
                                    padding: 14,
                                    display: "flex",
                                }}
                            >
                                <DescriptionIcon />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ fontSize: 12, color: "#888" }}>
                                    {t("map.activePermits").toUpperCase()}
                                </div>
                                <div style={{ fontSize: 28, fontWeight: 600 }}>
                                   { data.workpermitActive }
                                </div>

                            </div>
                        </Grid>
                    </GridCard>
                </Grid>
                <Grid item xs={12} sm={6} md={12} lg={6}>
                    <GridCard container alignItems="center">
                        <Grid item sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <div
                                style={{
                                    background: "#E8F5E9",
                                    borderRadius: "50%",
                                    padding: 14,
                                    display: "flex",
                                }}
                            >
                                <GroupsIcon />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ fontSize: 12, color: "#888" }}>
                                    {t("map.fieldWorkers").toUpperCase()}
                                </div>
                                <div style={{ fontSize: 28, fontWeight: 600 }}>
                                    { data.fieldWorkers }
                                </div>

                            </div>
                        </Grid>
                    </GridCard>
                </Grid>

                <Grid item xs={12} sm={6} md={12} lg={6}>
                    <GridCard container alignItems="center">
                        <Grid item sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <div
                                style={{
                                    background: "#FDECEA",
                                    borderRadius: "50%",
                                    padding: 14,
                                    display: "flex",
                                }}
                            >
                                <AssignmentLateIcon />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ fontSize: 12, color: "#888" }}>
                                    {t("map.suspendedPermits").toUpperCase()}
                                </div>
                                <div style={{ fontSize: 28, fontWeight: 600 }}>
                                    { data.workpermitInactive }
                                </div>

                            </div>
                        </Grid>
                    </GridCard>
                </Grid>

                <Grid item xs={12} sm={6} md={12} lg={6}>
                    <GridCard container alignItems="center">
                        <Grid item sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <div
                                style={{
                                    background: "#E0F7FA",
                                    borderRadius: "50%",
                                    padding: 14,
                                    display: "flex",
                                }}
                            >
                                <AirIcon />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ fontSize: 12, color: "#888" }}>
                                    {t("map.atmosphereMonitoring").toUpperCase()}
                                </div>
                                <div style={{ fontSize: 28, fontWeight: 600 }}>
                                    { data.gastTest }
                                </div>

                            </div>
                        </Grid>
                    </GridCard>
                </Grid>
                
            </Grid>

            <Grid container>
                <Grid item xs={12}>
                    <GridCard container justifyContent={'space-between'} alignItems="center">
                        <Grid item sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <div
                                style={{
                                    background: "#FFF3E0",
                                    borderRadius: "50%",
                                    padding: 14,
                                    display: "flex"
                                }}
                            >
                                <LockIcon sx={{ color: "#F57C00" }} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ fontSize: 12, color: "#888", fontWeight: 500 }}>
                                    {t("map.activeLotos").toUpperCase()}
                                </div>
                                <div style={{ fontSize: 28, fontWeight: 600 }}>
                                    { data.lotoActive }
                                </div>
                            </div>
                        </Grid>

                        <Divider sx={{ mx: 1 }} flexItem orientation="vertical" />

                        <Grid item sx={{ display: "flex", flexDirection: "column", gap: 1, marginRight: 5 }}>
                            <div style={{ fontSize: 12, color: "#888", fontWeight: 600 }}>
                                {t("map.equipmentDetail").toUpperCase()}
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ color: "#F57C00", fontWeight: 700 }}>
                                    { data.criticalAssets }
                                </span>
                                <span style={{ fontSize: 13, color: "#666" }}>
                                    {t("map.criticalEquipments")}
                                </span>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ color: "#F57C00", fontWeight: 700 }}>
                                    { data.nonCriticalAssets }
                                </span>
                                <span style={{ fontSize: 13, color: "#666" }}>
                                    {t("map.nonCriticalEquipments")}
                                </span>
                            </div>
                        </Grid>
                    </GridCard>
                </Grid>
            </Grid>

            <Grid container>
                <Grid item xs={12}>
                    <GridCard container display={'flex'} flexDirection={'column'} justifyItems="center" sx={{ padding: 1 }}>
                        <Grid item sx={{ gap: 1 }}>
                            <CustomTitleGraphics>{t("map.permitTypes")}</CustomTitleGraphics>
                            <Divider />
                            <ContainerCharts width='100%'>
                                {!noDataDonut ? 
                                    (
                                        <CardGraphic
                                            type="donut"
                                            height="100%"
                                            width="80%"
                                            options={{
                                                ...donutOptions,
                                            }}
                                            series={seriesWorkpermitType || []}
                                            chartRef={chartRef}
                                        />
                                    ) : 
                                    (<div style={{ margin: '0 auto', paddingTop: '40px' }}> {t('common.empty.noData')} </div>)
                                }
                            </ContainerCharts>
                        </Grid>
                    </GridCard>
                </Grid>
            </Grid>

            <Grid container>
                <Grid item xs={12}>
                    <GridCard container display={'flex'} flexDirection={'column'} justifyItems="center" sx={{ p: 1 }}>
                        <Grid item sx={{ gap: 1, width: "100%" }}>
                            <CustomTitleGraphics>{t("map.riskLevel")}</CustomTitleGraphics>
                            <Divider />
                            <ContainerCharts width='100%' sx={{ height: 150, p: 0.5 }}>
                                <Grid container display={"flex"} flexDirection={"row"} justifyContent={"space-between"} alignItems="start" sx={{ height: "100%" }}>
                                    <Grid item xs={!noDataRisk ? 7 : 12}>
                                        {!noDataRisk ? 
                                            (
                                                <CardGraphic
                                                    type="donut"
                                                    height={180}
                                                    options={{
                                                        ...radialOptions,
                                                    }}
                                                    series={seriesRisk}
                                                    chartRef={chartRef}
                                                />
                                            ) : 
                                            (<div style={{ textAlign: 'center', marginTop: '40px' }}> {t('common.empty.noData')} </div>)
                                        }
                                    </Grid>

                                    {!noDataRisk && (
                                        <Grid item xs={5} sx={{ mt: 2 }}>
                                            <CardGraphic
                                                type="bar"
                                                height={100}
                                                options={barOptions}
                                                series={barSeries}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </ContainerCharts>
                        </Grid>
                    </GridCard>
                </Grid>
            </Grid>

        </ContainerGraphics>
    )
}